import React, {useCallback, useEffect, useState} from 'react';
import clearIcon from '../../../icons/clear_icon.svg';
import {TypeaheadSearchProps} from "./props";
import {TypeAheadSearchLayout} from "./components/TypeAheadSearchLayout";

const getSearchValue = (search?: string) => search || '';

function TypeaheadSearch({
  options,
  search,
  placeholder,
  notFoundText,
  onSearchChange,
  onKeyUp,
  focus,
  enterKeyHint,
  role,
  onFocus,
  onMouseEnter,
  onBlur,
  onMouseLeave,
  onClickInput,
  onKeyDown,
  closeIcon = clearIcon,
  minCharacters = 0,
  hideClearButton = false,
  hideSuggestions = false,
  hideSearchIcon = true,
  inputType = 'text',
}: TypeaheadSearchProps) {
  const [searchedValue, setSearchedValue] = useState(() => {
    return getSearchValue(search);
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showClearButton, setShowClearButton] = useState(() => !!searchedValue?.length);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);

  useEffect(() => {
    const newSearch = getSearchValue(search);
    if (newSearch !== searchedValue) {
      setSearchedValue(newSearch);
    }
  }, [search]);

  useEffect(() => {
    if (!searchedValue) {
      setFilteredOptions([]);
      return;
    }
    const filteredSuggestions = options.filter(option =>
      option.toLowerCase().includes(searchedValue.toLowerCase())
    );
    setFilteredOptions(filteredSuggestions);
  }, [searchedValue, options]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);


  const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const length = value.length;
    setShowClearButton(length > 0);
    if (value === '') {
      handleClear();
      return;
    }
    setSearchedValue(value);
    setShowSuggestions(length >= minCharacters);
  }, [minCharacters]);

  const handleSuggestionClick = (suggestion: string) => {
    setSearchedValue(suggestion);
    setShowSuggestions(!showSuggestions);    
    onSearchChange?.(suggestion, handleClear);
  };

  const doSearch = () => {
    onSearchChange?.(searchedValue, handleClear);
  }

  const handleClear = () => {
    setSearchedValue('');
    setShowSuggestions(false);
    setShowClearButton(false);
  };
  const displayClear = showClearButton && !hideClearButton;
  const displaySearchIcon = showClearButton && !hideSearchIcon;
  const displaySuggestions = showSuggestions && !hideSuggestions;
  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyUp) {
      onKeyUp(e, handleClear);
    }
  }
  return (
    <TypeAheadSearchLayout
      placeholder={placeholder}
      notFoundText={notFoundText}
      enterKeyHint={enterKeyHint}
      role={role}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onBlur={onBlur}
      onMouseLeave={onMouseLeave}
      onClickInput={onClickInput}
      onKeyDown={onKeyDown}
      closeIcon={closeIcon}
      inputType={inputType}
      searchedValue={searchedValue}
      handleOnChange={handleOnChange}
      handleClear={handleClear}
      doSearch={doSearch}
      filteredOptions={filteredOptions}
      handleSuggestionClick={handleSuggestionClick}
      displayClear={displayClear}
      displaySearchIcon={displaySearchIcon}
      displaySuggestions={displaySuggestions}
      showSuggestions={showSuggestions}
      handleKeyUp={handleKeyUp}
      focus={focus}
    />
  );
}

export default React.memo(TypeaheadSearch);
